import React, { useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { getCroppedImg } from "utils/cropImage";

const ASPECT_OPTIONS = [
  { label: "Free", value: 0 },
  { label: "1:1", value: 1 },
  { label: "4:3", value: 4 / 3 },
  { label: "16:9", value: 16 / 9 },
];

// Build a centered crop for a given aspect (or a full-frame crop when free).
const buildCrop = (width, height, aspect) => {
  if (!aspect) {
    return { unit: "%", x: 0, y: 0, width: 100, height: 100 };
  }
  return centerCrop(
    makeAspectCrop({ unit: "%", width: 90 }, aspect, width, height),
    width,
    height
  );
};

const ImageCropModal = ({ open, imageSrc, fileName, onCancel, onCropComplete }) => {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [aspect, setAspect] = useState(1);
  const [processing, setProcessing] = useState(false);

  const onImageLoad = useCallback(
    (e) => {
      const { width, height } = e.currentTarget;
      setCrop(buildCrop(width, height, aspect));
    },
    [aspect]
  );

  const handleAspectChange = (_, val) => {
    if (val === null) return;
    setAspect(val);
    const img = imgRef.current;
    if (img) setCrop(buildCrop(img.width, img.height, val));
  };

  const handleConfirm = async () => {
    const img = imgRef.current;
    if (!img || !completedCrop?.width || !completedCrop?.height) return;
    try {
      setProcessing(true);
      const croppedFile = await getCroppedImg(img, completedCrop, fileName);
      onCropComplete(croppedFile);
    } catch (err) {
      console.error("Failed to crop image:", err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Crop Image</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={aspect}
            onChange={handleAspectChange}
          >
            {ASPECT_OPTIONS.map((opt) => (
              <ToggleButton key={opt.label} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#333",
            borderRadius: 1,
            p: 1,
            "& img": { maxHeight: 360, maxWidth: "100%" },
          }}
        >
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect || undefined}
              keepSelection
            >
              <img ref={imgRef} src={imageSrc} alt="crop" onLoad={onImageLoad} />
            </ReactCrop>
          )}
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
          Drag the corners or edges to adjust the crop. Drag inside to move it.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          className="create-btn"
          disabled={processing || !completedCrop?.width}
        >
          {processing ? "Processing..." : "Apply Crop"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropModal;
